import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Chip,
  Tooltip,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import DrawIcon from "@mui/icons-material/Draw";
import LinkIcon from "@mui/icons-material/Link";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import "./Tabledocumentswithsigners.css";

interface Signer {
  name: string;
  email: string;
  hasSigned: boolean;
  signedDate: string | null;
}

interface DocumentRow {
  documentOwner: string;
  name: string;
  data: string;
  dataAtualizacao?: string;
  id: string;
  status: string;
  signers: Signer[];
  isDocumentOwner: boolean;
}

interface TableDocumentsWithSignersProps {
  rows: DocumentRow[];
  editItem: (id: string) => void;
  viewDocument: (id: string) => void;
  downloadDocument: (id: string) => void;
  signDocumentToUser: (id: string) => void;
  resendEmail: (row: DocumentRow) => void;
  copySignerLinks: (id: string) => void;
  removeItem: (id: string) => void;
}

const formatSignedDate = (dateString: string | null): string => {
  if (!dateString) return "-";
  const date = new Date(dateString);
    // ajusta -3 horas
    date.setHours(date.getHours() - 3);
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

interface RowProps {
  row: DocumentRow;
  editItem: (id: string) => void;
  viewDocument: (id: string) => void;
  downloadDocument: (id: string) => void;
  signDocumentToUser: (id: string) => void;
  resendEmail: (row: DocumentRow) => void;
  copySignerLinks: (id: string) => void;
  removeItem: (id: string) => void;
}

const Row: React.FC<RowProps> = ({
  row,
  editItem,
  viewDocument,
  downloadDocument,
  signDocumentToUser,
  resendEmail,
  copySignerLinks,
  removeItem,
}) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [idCopied, setIdCopied] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const copyDocumentId = async (e: React.MouseEvent) => {
    e.stopPropagation();
    const id = row.id;
    try {
      await navigator.clipboard.writeText(id);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = id;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setIdCopied(true);
    window.setTimeout(() => setIdCopied(false), 2000);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Verificações de status para controle das ações
  const isAssinado = row.status === "Assinado";
  const isAguardando = row.status === "Aguardando Signatários";
  const isCancelado = row.status === "Cancelado";

  const getStatusChip = (status: string) => {
    const statusConfig: Record<string, { color: "success" | "warning" | "error" | "info" | "default"; label: string }> = {
      "Assinado": { color: "success", label: "Assinado" },
      "Aguardando Signatários": { color: "info", label: "Aguardando" },
      "Cancelado": { color: "error", label: "Cancelado" },
      "Pendente": { color: "info", label: "Pendente" },
    };

    const config = statusConfig[status] || { color: "default" as const, label: status };

    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        className={`status-chip status-chip-${config.color}`}
      />
    );
  };

  const signedCount = row.signers?.filter((s) => s.hasSigned).length || 0;
  const totalSigners = row.signers?.length || 0;

  return (
    <>
      <TableRow className="document-row">
        <TableCell className="expand-cell">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            className="expand-button"
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell className="owner-cell">{row.documentOwner}</TableCell>
        <TableCell className="document-name-cell">
          <span className="document-name">{row.name}</span>
        </TableCell>
        <TableCell className="date-cell">{row.data}</TableCell>
        <TableCell className="date-cell">
          {row.dataAtualizacao ?? "-"}
        </TableCell>
        <TableCell className="id-cell">
          <div className="document-id-cell-inner">
            <Tooltip title={row.id} arrow>
              <span className="document-id">{row.id.substring(0, 8)}...</span>
            </Tooltip>
            <Tooltip title={idCopied ? "Copiado!" : "Copiar ID completo"} arrow>
              <IconButton
                size="small"
                className="copy-id-button"
                aria-label="Copiar ID do documento"
                onClick={copyDocumentId}
              >
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell className="signers-count-cell">
          {totalSigners === 1 ? (
            <div className="single-signer-inline">
              <div className="single-signer-info">
                {row.signers[0].hasSigned ? (
                  <CheckCircleIcon className="signer-icon signed" style={{ fontSize: 18 }} />
                ) : (
                  <PendingIcon className="signer-icon pending" style={{ fontSize: 18 }} />
                )}
                <span className="single-signer-email">{row.signers[0].name || row.signers[0].email}</span>
              </div>
            </div>
          ) : (
            <div className="signers-progress">
              <span className={`progress-text ${signedCount === totalSigners && totalSigners > 0 ? 'complete' : ''}`}>
                {signedCount}/{totalSigners}
              </span>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: totalSigners > 0 ? `${(signedCount / totalSigners) * 100}%` : "0%",
                  }}
                />
              </div>
            </div>
          )}
        </TableCell>
        <TableCell className="status-cell">{getStatusChip(row.status)}</TableCell>
        <TableCell className="actions-cell">
          <IconButton
            onClick={handleMenuClick}
            className="menu-button"
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            className="actions-menu"
          >
            {/* Visualizar - sempre disponível */}
            <MenuItem
              onClick={() => {
                viewDocument(row.id);
                handleMenuClose();
              }}
            >
              <VisibilityIcon fontSize="small" className="menu-icon" />
              Visualizar
            </MenuItem>

            {/* Editar - removido */}

            {/* Download - apenas quando status for "Assinado" */}
            {isAssinado && (
              <MenuItem
                onClick={() => {
                  downloadDocument(row.id);
                  handleMenuClose();
                }}
              >
                <DownloadIcon fontSize="small" className="menu-icon" />
                Download
              </MenuItem>
            )}

            {/* Assinar - apenas se estiver aguardando */}
            {isAguardando && (
              <MenuItem
                onClick={() => {
                  signDocumentToUser(row.id);
                  handleMenuClose();
                }}
              >
                <DrawIcon fontSize="small" className="menu-icon" />
                Assinar
              </MenuItem>
            )}

            {/* Signatários - links + reenviar email */}
            {row.isDocumentOwner && !isAssinado && !isCancelado && (
              <MenuItem
                onClick={() => {
                  copySignerLinks(row.id);
                  handleMenuClose();
                }}
              >
                <LinkIcon fontSize="small" className="menu-icon" />
                Signatários
              </MenuItem>
            )}

            {/* Excluir - apenas se for owner e não estiver assinado */}
            {row.isDocumentOwner && !isAssinado && (
              <MenuItem
                onClick={() => {
                  removeItem(row.id);
                  handleMenuClose();
                }}
                className="delete-menu-item"
              >
                <DeleteIcon fontSize="small" className="menu-icon" />
                Excluir
              </MenuItem>
            )}
          </Menu>
        </TableCell>
      </TableRow>
      <TableRow className="collapse-row">
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className="signers-container">
              <div className="signers-header">
                <span className="signers-title">Signatários</span>
              </div>
              <div className="signers-list">
                {row.signers?.map((signer, index) => (
                  <div key={index} className="signer-card">
                    <div className="signer-info">
                      <div className="signer-icon-wrapper">
                        {signer.hasSigned ? (
                          <CheckCircleIcon className="signer-icon signed" />
                        ) : (
                          <PendingIcon className="signer-icon pending" />
                        )}
                      </div>
                      <div className="signer-details">
                        <span className="signer-email">{signer.name || signer.email}</span>
                        <span className={`signer-status ${signer.hasSigned ? 'signed' : 'pending'}`}>
                          {signer.hasSigned
                            ? `Assinado em ${formatSignedDate(signer.signedDate)}`
                            : "Aguardando assinatura"}
                        </span>
                      </div>
                    </div>
                    <div className={`signer-badge ${signer.hasSigned ? 'signed' : 'pending'}`}>
                      {signer.hasSigned ? "Assinado" : "Pendente"}
                    </div>
                  </div>
                ))}
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const TableDocumentsWithSigners: React.FC<TableDocumentsWithSignersProps> = ({
  rows,
  editItem,
  viewDocument,
  downloadDocument,
  signDocumentToUser,
  resendEmail,
  copySignerLinks,
  removeItem,
}) => {
  return (
    <TableContainer component={Paper} className="table-documents-container">
      <Table aria-label="documents table" className="documents-table">
        <TableHead>
          <TableRow className="table-header-row">
            <TableCell className="header-cell expand-header" />
            <TableCell className="header-cell">Proprietário</TableCell>
            <TableCell className="header-cell">Documento</TableCell>
            <TableCell className="header-cell">Data de Criação</TableCell>
            <TableCell className="header-cell">Data de Atualização</TableCell>
            <TableCell className="header-cell">ID Documento</TableCell>
            <TableCell className="header-cell">Signatários</TableCell>
            <TableCell className="header-cell">Status</TableCell>
            <TableCell className="header-cell actions-header">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((row) => (
              <Row
                key={row.id}
                row={row}
                editItem={editItem}
                viewDocument={viewDocument}
                downloadDocument={downloadDocument}
                signDocumentToUser={signDocumentToUser}
                resendEmail={resendEmail}
                copySignerLinks={copySignerLinks}
                removeItem={removeItem}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={9} className="empty-cell">
                <div className="empty-state">
                  <span>Nenhum documento encontrado</span>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TableDocumentsWithSigners;